import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Radio,
  Tag,
  Badge,
  message,
  Divider,
} from "antd";
import {
  LeftOutlined,
  RightOutlined,
  PlusOutlined,
  CalendarOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  CalendarFilled,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
import MonthView from "./MonthView";
import WeekView from "./WeekView";
import EventModal from "./EventModal";
import DayView from "./DayView";
import AgendaView from "./AgendaView";
import YearView from "./YearView";
import api from "../../../services/api";
import CommonSelect from "../../../core/common/commonSelect";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";

// Day.js plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

export interface Event {
  id: string;
  title: string;
  description?: string;
  start: Dayjs;
  end: Dayjs;
  allDay: boolean;
  category: string;
  color: string;
  location?: string;
  reminders?: string[];
  is_public?: boolean;
  is_generated?: boolean;
  original_start?: string;
}

export interface CalendarCategory {
  id: string;
  name: string;
  color: string;
  visible: boolean;
}

interface RequestData {
  signature: string | null;
  company_id?: string;
  company_type?: string;
}

const ActivityCalendar: React.FC = () => {
  const signature = localStorage.getItem("signature");
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
  const [view, setView] = useState<
    "year" | "month" | "week" | "day" | "agenda"
  >("month");
  const [events, setEvents] = useState<Event[]>([]);
  const [categories, setCategories] = useState<CalendarCategory[]>([
    { id: "1", name: "Meeting", color: "#1890ff", visible: true },
    { id: "2", name: "Holiday", color: "#ff4d4f", visible: true },
    { id: "3", name: "Birthday", color: "#52c41a", visible: true },
    { id: "4", name: "Appointment", color: "#faad14", visible: true },
    { id: "5", name: "Anniversary", color: "#722ed1", visible: true },
    { id: "6", name: "Reminder", color: "#13c2c2", visible: true },
  ]);
  const [isEventModalVisible, setIsEventModalVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [formDataCompanyChoose, setFormDataCompanyChoose] = useState({
    companyId: "",
    companyName: "Select Company",
    companyType: "",
  });

  // Prepare company options for CommonSelect
  const companyChoose = companies.map((company: any) => ({
    value: company.id.toString(),
    label: `${company.name} - ${company.type}`,
    companyName: company.name,
  }));

  const fetchCompanies = async () => {
    try {
      const response = await api.post("userService/getAllCompanyList", {
        signature,
      });
      const companiesData = response.data?.data?.data || [];
      setCompanies(companiesData);

      if (companiesData.length > 0) {
        // Auto-select the first company
        const firstCompany = companiesData[0];
        handleCompanyChange({
          value: firstCompany.id.toString(),
          label: `${firstCompany.name} - ${firstCompany.type}`,
          companyName: firstCompany.name,
        });
      }
    } catch (err) {
      console.error("Error fetching companies:", err);
    }
  };

  const handleCompanyChange = async (selectedOption: any) => {
    if (!selectedOption) return;

    const newFormData = {
      companyId: selectedOption.value,
      companyName:
        selectedOption.companyName ||
        selectedOption.label.split("-")[0]?.trim() ||
        "All Companies",
      companyType: selectedOption.label.split("-")[1]?.trim() || "",
    };

    setFormDataCompanyChoose((prev: any) => ({
      ...prev,
      ...newFormData,
    }));
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (formDataCompanyChoose.companyId && formDataCompanyChoose.companyType) {
      fetchEvents();
    }
  }, [formDataCompanyChoose.companyId, formDataCompanyChoose.companyType]);

  const fetchEvents = async () => {
    try {
      const requestData: RequestData = { signature };

      if (
        formDataCompanyChoose.companyId &&
        formDataCompanyChoose.companyType
      ) {
        requestData.company_id = formDataCompanyChoose.companyId;
        requestData.company_type = formDataCompanyChoose.companyType;
      }

      const [publicRes, privateRes] = await Promise.all([
        api.post("activityCalendarService/getAllPublicEvents", requestData),
        api.post("activityCalendarService/getAllPrivateEvents", requestData),
      ]);

      const publicEvents = publicRes.data?.message?.[0]?.data || [];
      const privateEvents = privateRes.data?.message?.[0]?.data || [];

      const allEvents = [...publicEvents, ...privateEvents].filter(
        (e) => e.status === 1
      );

      const mappedEvents: Event[] = allEvents.map((e: any) => {
        const category = categories.find(
          (c) => c.id === e.event_type.toString()
        );
        const isHoliday = category?.name === "Holiday";

        return {
          id: e.id.toString(),
          title: e.title,
          description: e.description,
          start: dayjs(e.start_date),
          end: dayjs(e.end_date),
          allDay: e.is_all_day,
          category: category?.name || "Meeting",
          color: category?.color || "#1890ff",
          is_public: e.is_public,
          is_generated: e.is_generated || isHoliday || false,
          original_start: e.start_date,
        };
      });
      setEvents(mappedEvents);
    } catch (err) {
      console.error("Error fetching events:", err);
      message.error("Failed to fetch events.");
    }
  };

  const generateRecurringEvents = (
    baseEvents: Event[],
    currentYear: number
  ): Event[] => {
    const recurringEvents: Event[] = [];

    baseEvents.forEach((event) => {
      if (event.is_generated) {
        const originalYear = event.start.year();

        // For anniversary events, skip if it's the same year as joining
        if (event.category === "Anniversary" && event.original_start) {
          const joinYear = dayjs(event.original_start).year();

          if (currentYear === joinYear) {
            return; // Skip event for the current year
          }

          if (currentYear > joinYear) {
            const yearsOfService = currentYear - joinYear;
            const newEvent = {
              ...event,
              start: event.start.year(currentYear),
              end: event.end.year(currentYear),
              description: `Work anniversary of ${event.title.split("'s")[0]} - ${yearsOfService} years`,
            };
            recurringEvents.push(newEvent);
          }
        } else if (event.category === "Birthday" || event.category === "Holiday") {
          if (currentYear >= originalYear) {
            const newEvent = {
              ...event,
              start: event.start.year(currentYear),
              end: event.end.year(currentYear),
            };
            recurringEvents.push(newEvent);
          }
        }
      } else {
        recurringEvents.push(event);
      }
    });

    return recurringEvents;
  };

  const handlePrevious = () => {
    const unit =
      view === "year"
        ? "year"
        : view === "month"
          ? "month"
          : view === "week"
            ? "week"
            : "day";
    setCurrentDate(currentDate.subtract(1, unit));
  };

  const handleNext = () => {
    const unit =
      view === "year"
        ? "year"
        : view === "month"
          ? "month"
          : view === "week"
            ? "week"
            : "day";
    setCurrentDate(currentDate.add(1, unit));
  };

  const handleToday = () => {
    setCurrentDate(dayjs());
  };

  const handleViewChange = (
    newView: "year" | "month" | "week" | "day" | "agenda"
  ) => {
    setView(newView);
  };

  const handleEventCreate = () => {
    setSelectedEvent(null);
    setIsEventModalVisible(true);
  };

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setIsEventModalVisible(true);
  };

  const handleMonthClick = (month: Dayjs) => {
    setCurrentDate(month);
    setView("month");
  };

  const handleDateClick = (date: Dayjs) => {
    setCurrentDate(date);
    setView("day");
  };

  const handleCategoryToggle = (categoryId: string) => {
    setCategories(
      categories.map((cat) =>
        cat.id === categoryId ? { ...cat, visible: !cat.visible } : cat
      )
    );
  };

  const filteredEvents = generateRecurringEvents(
    events.filter((event) => {
      const category = categories.find((cat) => cat.name === event.category);
      return category?.visible !== false;
    }),
    currentDate.year()
  );

  const getViewTitle = () => {
    switch (view) {
      case "year":
        return currentDate.format("YYYY");
      case "month":
        return currentDate.format("MMMM YYYY");
      case "week":
        return `Week of ${currentDate.startOf("week").format("MMM D")}`;
      case "day":
        return currentDate.format("MMMM D, YYYY");
      default:
        return currentDate.format("MMMM YYYY");
    }
  };

  return (
    <div
      className="page-wrapper"
    >
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Activity Calendar</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Activity Calendar</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Events
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div
              className="dropdown me-2 mb-2"
              title="Choose Company"
              style={{ minWidth: "200px" }}
            >
              <CommonSelect
                className="select"
                options={companyChoose}
                defaultValue={
                  formDataCompanyChoose.companyId
                    ? {
                      value: `${formDataCompanyChoose.companyId} - ${formDataCompanyChoose.companyType}`,
                      label: `${formDataCompanyChoose.companyName} - ${formDataCompanyChoose.companyType}`,
                    }
                    : companyChoose[0]
                }
                onChange={handleCompanyChange}
              />
            </div>

            <div className="ms-2 head-icons">
              <CollapseHeader />
            </div>
          </div>
        </div>

        {/* Calendar Controls */}
        <Card style={{ marginBottom: 20 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={8}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Button onClick={handleToday} type="default">
                  Today
                </Button>
                <Button.Group>
                  <Button icon={<LeftOutlined />} onClick={handlePrevious} />
                  <Button icon={<RightOutlined />} onClick={handleNext} />
                </Button.Group>
                {view === "day" ? (
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: "16px",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {currentDate.format("dddd, MMMM ")}
                    <span
                      className={`day-number ${currentDate.isSame(dayjs(), "day") ? "today" : ""
                        }`}
                    >
                      {currentDate.format("D")}
                    </span>
                    {currentDate.format(", YYYY")}
                  </span>
                ) : (
                  <span style={{ fontWeight: "bold", fontSize: "16px" }}>
                    {getViewTitle()}
                  </span>
                )}
              </div>
            </Col>

            <Col xs={24} md={8} style={{ textAlign: "center" }}>
              <Radio.Group
                value={view}
                onChange={(e) => handleViewChange(e.target.value)}
              >
                <Radio.Button value="year">
                  <CalendarFilled /> Year
                </Radio.Button>
                <Radio.Button value="month">
                  <AppstoreOutlined /> Month
                </Radio.Button>
                <Radio.Button value="week">
                  <CalendarOutlined /> Week
                </Radio.Button>
                <Radio.Button value="day">
                  <CalendarOutlined /> Day
                </Radio.Button>
                <Radio.Button value="agenda">
                  <UnorderedListOutlined /> Agenda
                </Radio.Button>
              </Radio.Group>
            </Col>

            <Col xs={24} md={8} style={{ textAlign: "right" }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleEventCreate}
              >
                New Event
              </Button>
            </Col>
          </Row>

          {/* Category Filters */}
          <Divider style={{ margin: "16px 0" }} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {categories.map((category) => (
              <Badge
                key={category.id}
                color={category.color}
                text={category.name}
                style={{ cursor: "pointer" }}
                onClick={() => handleCategoryToggle(category.id)}
              >
                <Tag
                  color={category.visible ? category.color : "default"}
                  style={{ cursor: "pointer" }}
                >
                  {category.name}
                </Tag>
              </Badge>
            ))}
          </div>
        </Card>

        {/* Calendar View */}
        <Card>
          {view === "year" && (
            <YearView
              currentDate={currentDate}
              events={filteredEvents}
              selectedCompany={{
                id: formDataCompanyChoose.companyId,
                type: formDataCompanyChoose.companyType,
              }}
              onEventClick={handleEventClick}
              onMonthClick={handleMonthClick}
            />
          )}
          {view === "month" && (
            <MonthView
              currentDate={currentDate}
              events={filteredEvents}
              categories={categories}
              selectedCompany={{
                id: formDataCompanyChoose.companyId,
                type: formDataCompanyChoose.companyType,
              }}
              onEventClick={handleEventClick}
              onDateClick={handleDateClick}
              onEventsChange={setEvents}
              onEventSaved={fetchEvents}
            />
          )}
          {view === "week" && (
            <WeekView
              currentDate={currentDate}
              events={filteredEvents}
              categories={categories}
              selectedCompany={{
                id: formDataCompanyChoose.companyId,
                type: formDataCompanyChoose.companyType,
              }}
              onEventClick={handleEventClick}
              onDateClick={handleDateClick}
              onEventsChange={setEvents}
              onEventSaved={fetchEvents}
            />
          )}
          {view === "day" && (
            <DayView
              currentDate={currentDate}
              events={filteredEvents}
              categories={categories}
              selectedCompany={{
                id: formDataCompanyChoose.companyId,
                type: formDataCompanyChoose.companyType,
              }}
              onEventClick={handleEventClick}
              onEventsChange={setEvents}
              onEventSaved={fetchEvents}
            />
          )}
          {view === "agenda" && (
            <AgendaView
              currentDate={currentDate}
              events={filteredEvents}
              selectedCompany={{
                id: formDataCompanyChoose.companyId,
                type: formDataCompanyChoose.companyType,
              }}
              onEventClick={handleEventClick}
            />
          )}
        </Card>

        {/* Event Modal */}
        <EventModal
          visible={isEventModalVisible}
          event={selectedEvent}
          categories={categories}
          selectedCompany={{
            id: formDataCompanyChoose.companyId,
            type: formDataCompanyChoose.companyType,
          }}
          onCancel={() => setIsEventModalVisible(false)}
          onSave={async (eventData) => {
            const categoryId =
              categories.find((c) => c.name === eventData.category)?.id || "1";
            const data: any = {
              signature,
              title: eventData.title,
              description: eventData.description,
              start_date: eventData.start.format("YYYY-MM-DD HH:mm:ss"),
              end_date: eventData.end.format("YYYY-MM-DD HH:mm:ss"),
              is_all_day: eventData.allDay,
              event_type: parseInt(categoryId),
              is_public: eventData.is_public || false,
            };
            try {
              if (eventData.id) {
                await api.post("activityCalendarService/updateEvent", {
                  ...data,
                  id: eventData.id,
                });
                message.success("Event updated successfully");
              } else {
                await api.post("activityCalendarService/createEvent", data);
                message.success("Event created successfully");
              }
              fetchEvents();
              setIsEventModalVisible(false);
            } catch (err) {
              message.error("Failed to save event");
            }
          }}
        />
      </div>

      {/* Footer  */}
      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">2025 © CMS.</p>
        <p>
          Designed & Developed By{" "}
          <Link to="#" className="text-primary">
            ZYVO
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ActivityCalendar;
